import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";

/**
 * /categories — index page showing all 12 photo genres with cover thumbnail
 * (first photo in each category) + photo count.
 */
export default function CategoryIndex() {
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const [thumbs, setThumbs] = useState({});

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
  }, []);

  // Fetch one cover thumb per category (parallel).
  useEffect(() => {
    if (!cats.length) return;
    let cancelled = false;
    Promise.all(
      cats.map((c) =>
        c.photo_count > 0
          ? api.get(`/photos?category=${c.slug}&limit=1`).then((r) => [c.slug, r.data[0]])
          : Promise.resolve([c.slug, null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      const m = {};
      for (const [slug, p] of pairs) m[slug] = p;
      setThumbs(m);
    });
    return () => { cancelled = true; };
  }, [cats]);

  return (
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-16" data-testid="category-index-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
        {t("brand")}
      </div>
      <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl text-zinc-50 tracking-tight">
        {t("genres")}
      </h1>
      <p className="mt-4 max-w-2xl text-zinc-400 font-light leading-relaxed">
        {t("genres_lead")}
      </p>

      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {cats.map((c) => {
          const cover = thumbs[c.slug];
          const thumb = cover
            ? `${process.env.REACT_APP_BACKEND_URL}${cover.thumb_url || cover.image_url}`
            : null;
          return (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group block border border-zinc-900 hover:border-zinc-700 transition-colors"
              data-testid={`category-tile-${c.slug}`}
            >
              <div className="aspect-[4/5] relative overflow-hidden bg-zinc-900">
                {thumb ? (
                  <img
                    src={thumb}
                    alt={t(`cat_${c.slug}`)}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 font-mono text-[10px] uppercase tracking-[0.3em]">—</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="font-serif text-2xl text-zinc-50 font-light leading-tight">
                    {t(`cat_${c.slug}`)}
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                    {c.photo_count} {c.photo_count === 1 ? "photo" : "photos"}
                  </div>
                  <p
                    className="mt-2 text-zinc-300 font-light text-xs leading-snug line-clamp-2"
                    data-testid={`category-tile-desc-${c.slug}`}
                  >
                    {t(`cat_${c.slug}_desc`)}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
